/**
 * B1124 (Eric 2026-09-08) — Coût RÉEL mesuré des appels IA.
 *
 * POURQUOI
 * ────────
 * Tous les montants affichés dans Knoria étaient des estimations par moyenne
 * (« 7 appels × 0,02 $ »). Impossible de voir qu'un prompt a doublé de taille,
 * impossible de savoir quelle catégorie coûte cher.
 *
 * HONNÊTETÉ DU CHIFFRE
 * ────────────────────
 * Les fournisseurs ne renvoient PAS de montant en dollars. Ce qui est mesuré,
 * appel par appel : les jetons en entrée et en sortie, et les minutes d'audio.
 * Le montant est cette consommation réelle × le tarif public du modèle. Le
 * panneau le dit explicitement — laisser croire à une facture serait pire que
 * l'ancienne estimation.
 */
import { useCallback, useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { Coins } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// Montants en français : virgule décimale. 4 décimales sous 1 $, sinon un
// appel à 0,0013 $ s'afficherait « 0,00 $ » et passerait pour gratuit.
const usd = (n) => {
  const v = Number(n || 0);
  const digits = v > 0 && v < 1 ? 4 : 2;
  return `${v.toLocaleString("fr-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits })} $ US`;
};
const num = (n) => Number(n || 0).toLocaleString("fr-CA");
const when = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso || "");
  }
};

export const MeasuredAICosts = () => {
  const { tr } = useLang();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API}/api/org/ai-package/measured-costs`,
        { params: { days: d }, withCredentials: true }
      );
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Coûts mesurés indisponibles", "Measured costs unavailable"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5 mt-6" data-testid="measured-ai-costs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
          <Coins size={18} className="text-[#002FA7]" />
          {tr("Coût réel mesuré des appels IA", "Measured cost of AI calls")}
        </h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-sm border border-zinc-300 rounded-lg px-2 py-1.5 bg-white"
          data-testid="measured-costs-period"
        >
          <option value={7}>{tr("7 derniers jours", "Last 7 days")}</option>
          <option value={30}>{tr("30 derniers jours", "Last 30 days")}</option>
          <option value={90}>{tr("90 derniers jours", "Last 90 days")}</option>
          <option value={365}>12 derniers mois</option>
        </select>
      </div>

      {loading && (
        <p className="text-xs text-zinc-500 mt-3" data-testid="measured-costs-loading">{tr("Chargement…", "Loading…")}</p>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <div className="rounded-lg border border-zinc-200 p-3" data-testid="measured-costs-total">
              <p className="text-[11px] uppercase tracking-wide text-zinc-500">{tr("Total mesuré", "Measured total")}</p>
              <p className="text-lg font-semibold text-zinc-900">{usd(data.usd_total)}</p>
            </div>
            <div className="rounded-lg border border-zinc-200 p-3" data-testid="measured-costs-calls">
              <p className="text-[11px] uppercase tracking-wide text-zinc-500">{tr("Appels enregistrés", "Recorded calls")}</p>
              <p className="text-lg font-semibold text-zinc-900">{num(data.calls)}</p>
            </div>
            <div className="rounded-lg border border-zinc-200 p-3" data-testid="measured-costs-tokens">
              <p className="text-[11px] uppercase tracking-wide text-zinc-500">{tr("Jetons entrée / sortie", "Input / output tokens")}</p>
              <p className="text-sm font-semibold text-zinc-900">
                {num(data.input_tokens)} / {num(data.output_tokens)}
              </p>
            </div>
            <div className="rounded-lg border border-zinc-200 p-3" data-testid="measured-costs-audio">
              <p className="text-[11px] uppercase tracking-wide text-zinc-500">{tr("Minutes d'audio", "Audio minutes")}</p>
              <p className="text-lg font-semibold text-zinc-900">{data.audio_minutes}</p>
            </div>
          </div>

          {data.calls === 0 ? (
            <p className="text-xs text-zinc-500 mt-4" data-testid="measured-costs-empty">
              {tr(
                "Aucun appel IA enregistré sur cette période. La mesure démarre au prochain appel : les appels antérieurs à cette fonctionnalité n'ont pas été comptés.",
                "No AI call recorded over this period. Measuring starts on the next call: calls made before this feature were never counted.",
              )}
            </p>
          ) : (
            <div className="grid lg:grid-cols-2 gap-5 mt-5">
              <section data-testid="measured-costs-by-source">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 mb-2">
                  {tr("Par fonctionnalité", "By feature")}
                </h3>
                <ul className="space-y-1">
                  {data.by_source.map((b) => (
                    <li key={b.key} className="flex items-center justify-between text-sm border-b border-zinc-100 py-1">
                      <span className="text-zinc-700">{b.key}</span>
                      <span className="text-zinc-900 font-medium">
                        {usd(b.usd)} <span className="text-zinc-400 text-xs">({b.calls})</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
              <section data-testid="measured-costs-by-model">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 mb-2">{tr("Par modèle", "By model")}</h3>
                <ul className="space-y-1">
                  {data.by_model.map((b) => (
                    <li key={b.key} className="flex items-center justify-between text-sm border-b border-zinc-100 py-1">
                      <span className="text-zinc-700 font-mono text-xs">{b.key}</span>
                      <span className="text-zinc-900 font-medium">
                        {usd(b.usd)} <span className="text-zinc-400 text-xs">({b.calls})</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}

          {data.recent?.length > 0 && (
            <details className="mt-5" data-testid="measured-costs-recent">
              <summary className="cursor-pointer text-xs font-semibold text-zinc-600 hover:text-zinc-900">
                {tr("Derniers appels", "Latest calls")} ({data.recent.length})
              </summary>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500 border-b border-zinc-200">
                      <th className="py-1 pr-3">{tr("Quand", "When")}</th>
                      <th className="py-1 pr-3">{tr("Fonctionnalité", "Feature")}</th>
                      <th className="py-1 pr-3">{tr("Modèle", "Model")}</th>
                      <th className="py-1 pr-3">{tr("Entrée", "Input")}</th>
                      <th className="py-1 pr-3">{tr("Sortie", "Output")}</th>
                      <th className="py-1">{tr("Coût", "Cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((c) => (
                      <tr key={c.id} className="border-b border-zinc-100" data-testid={`measured-call-${c.id}`}>
                        <td className="py-1 pr-3 text-zinc-600 whitespace-nowrap">{when(c.at)}</td>
                        <td className="py-1 pr-3 text-zinc-700">{c.source || "—"}</td>
                        <td className="py-1 pr-3 font-mono text-zinc-600">{c.model}</td>
                        <td className="py-1 pr-3">{c.kind === "audio" ? `${c.minutes} min` : num(c.input_tokens)}</td>
                        <td className="py-1 pr-3">{c.kind === "audio" ? "—" : num(c.output_tokens)}</td>
                        <td className="py-1 font-medium">{usd(c.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <p className="mt-4 px-3 py-2 rounded bg-zinc-50 border border-zinc-200 text-xs text-zinc-600"
             data-testid="measured-costs-note">
            {data.measure_note}
          </p>
        </>
      )}
    </div>
  );
};

export default MeasuredAICosts;
